import FeatureGates from '@atlaskit/feature-gate-js-client';
import { Route } from 'react-resource-router';

import { Apps } from '@townsquare/app-context';
import { getConfig } from '@townsquare/config';
import { createSearchParamsFromCurrentUrl } from '@townsquare/search-params';
import { TEAMS_AS_A_STANDALONE_APP } from '@townsquare/stat-sig';
import { isGateEnabled } from '@townsquare/stat-sig/gate';

import type {
  AccessControlCapability,
  CapabilityContainer,
} from './CapabilityRouter/__generated__/CapabilityRouterFragment.graphql';
import type { RouteWithOutlet, RouteWithSubproduct, UserCapabilityType } from './types';

export function isRouteWithOutlet(route: Route): route is RouteWithOutlet {
  return Object.prototype.hasOwnProperty.call(route, 'type');
}

export function isRouteWithSubproduct(route: Route): route is RouteWithSubproduct {
  return Object.prototype.hasOwnProperty.call(route, 'subproduct');
}

export const getRoleFromCapability = (
  capabilityContainer: CapabilityContainer,
  capability: AccessControlCapability,
): UserCapabilityType => {
  return `${capabilityContainer.toLowerCase()}:${capability.toLowerCase()}` as UserCapabilityType;
};

export const ORG_SITE_ROUTE_REGEX = new RegExp(
  `^/o/(?<orgIdentifier>[0-9a-zA-Z-]+)(/s/(?<siteIdentifier>[a-zA-Z0-9%+-]+))*`,
);

// Strips the URL to get route path
// eg. `https://home.stg.atlassian.com/o/{orgId}/s/{siteId}/goals/{extra}` to `/goals/{extra}`
export const getRoutePathFromUrl = (url: string) =>
  url.replace(getConfig().fullUrl, '').replace(ORG_SITE_ROUTE_REGEX, '');

export const isGoalsAppRoute = (url: string) => {
  if (!isGateEnabled('atlas_goals_app_enabled')) {
    return false;
  }

  return getRoutePathFromUrl(url).startsWith('/goal');
};

export const isTeamsAppRoute = (url: string) => {
  if (
    !(
      FeatureGates.initializeCompleted() && FeatureGates.getExperimentValue(TEAMS_AS_A_STANDALONE_APP, 'enabled', false)
    )
  ) {
    return false;
  }

  // Check if the noWorkspace parameter is set to true
  // This is a temporary hack to redirect no workspace users to the home app until titan dependency is removed
  const searchParams = createSearchParamsFromCurrentUrl();
  const noWorkspace = searchParams.get('homeRedirect') === 'true';

  return getRoutePathFromUrl(url).startsWith('/people') && !noWorkspace;
};

/**
 *
 * @param route URL or path of a link ie. https://home.stg.atlassian.com/o/{orgId}/s/{siteId}/goals` or `/goals`
 * @param app App ie. Goals, Teams, Home
 * @returns if route belongs to app
 */
export const isRouteOfApp = (route: string, app: Apps) => {
  if (isGoalsAppRoute(route)) {
    return app === 'goals';
  }

  if (isTeamsAppRoute(route)) {
    return app === 'teams';
  }

  return app === 'home';
};
